import { FileText, ChevronDown, ChevronRight, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { testIds } from '@/lib/test-ids'

export type ModifiedFileItem = {
  /** Internal storage path (e.g. ws/{orgId}/{slug}.md) - never render directly; use displayPath. */
  filePath: string
  /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend, when available. */
  displayPath?: string | null
  /** Full shareable webapp URL, when available. */
  url?: string | null
  status: 'new' | 'modified'
  linesAdded: number
  linesRemoved: number
  documentId?: string
  /**
   * True while a proposed_edit still needs Accept/Reject.
   * False/undefined after modal Accept applied the write (or direct write) - Keep check hides.
   */
  pendingReview?: boolean
}

const ACTION_BTN =
  'h-7 shrink-0 text-xs px-1.5 @[300px]/files-bar:px-2'

/**
 * Files changed bar (Cursor-style) above the chat composer.
 * Collapsed header stays one line on a skinny agent rail: count only, short actions.
 */
export function CollapsibleModifiedFilesPanel({
  expanded,
  onToggle,
  files,
  onFileClick,
  onUndoAll,
  onKeepAll,
  onReview,
  onUndoFile,
  onKeepFile,
  /** When false, hide Review (no pending proposed_edit diffs left). */
  showReview = true,
  /** When false, the in-document review strip owns Accept and Reject. */
  showDecisions = true,
}: {
  expanded: boolean
  onToggle: () => void
  files: ModifiedFileItem[]
  onFileClick?: (filePath: string, file?: ModifiedFileItem) => void
  onUndoAll?: () => void
  onKeepAll?: () => void
  onReview?: () => void
  onUndoFile?: (filePath: string) => void
  onKeepFile?: (filePath: string) => void
  showReview?: boolean
  showDecisions?: boolean
}) {
  const count = files.length
  const countTitle = `${count} ${count === 1 ? 'file' : 'files'}`
  return (
    <div
      className="@container/files-bar flex-shrink-0 border-t border-l border-r border-border rounded-t-lg bg-muted/40 flex flex-col"
      data-testid={testIds.chatPane.modifiedFilesPanel}
    >
      <div className="flex w-full flex-nowrap items-center gap-1 px-2 py-1.5 @[300px]/files-bar:gap-2 @[300px]/files-bar:px-3 @[300px]/files-bar:py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 items-center gap-1 rounded p-1 text-left hover:bg-muted/30 transition-colors @[300px]/files-bar:gap-1.5"
          title={expanded ? 'Collapse file list' : `Show ${countTitle}`}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <span className="text-sm font-medium tabular-nums whitespace-nowrap text-muted-foreground">
            {count}
            <span className="hidden @[360px]/files-bar:inline">
              {count === 1 ? ' file' : ' files'}
            </span>
          </span>
        </button>
        {showDecisions ? (
        <div className="ml-auto flex shrink-0 items-center gap-0.5 @[300px]/files-bar:gap-1">
          <Button
            variant="ghost"
            size="sm"
            className={ACTION_BTN}
            onClick={onUndoAll}
            title="Undo all"
            aria-label="Undo all"
            data-testid={testIds.chatPane.modifiedFilesUndo}
          >
            Undo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={ACTION_BTN}
            onClick={onKeepAll}
            title="Keep all"
            aria-label="Keep all"
            data-testid={testIds.chatPane.modifiedFilesKeep}
          >
            Keep
          </Button>
          {showReview ? (
            <Button
              size="sm"
              className={ACTION_BTN}
              onClick={onReview}
              title="Review"
              aria-label="Review"
              data-testid={testIds.chatPane.modifiedFilesReview}
            >
              Review
            </Button>
          ) : null}
        </div>
        ) : null}
      </div>
      {expanded && (
        <div className="max-h-[240px] overflow-y-auto divide-y divide-border bg-transparent border-t border-border">
          {files.map((file, idx) => (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              onClick={() => onFileClick?.(file.filePath, file)}
              onKeyDown={(e) => e.key === 'Enter' && onFileClick?.(file.filePath, file)}
              className="flex items-center gap-2 px-2 py-2 hover:bg-muted/30 transition-colors cursor-pointer group @[300px]/files-bar:gap-3 @[300px]/files-bar:px-3"
              title={file.displayPath || file.url || undefined}
            >
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-xs flex-1 min-w-0 truncate">
                {(file.displayPath || file.filePath).split('/').pop()}
              </span>
              <div className="flex shrink-0 items-center gap-1.5 text-xs @[300px]/files-bar:gap-2">
                {file.status === 'new' ? (
                  <span className="text-muted-foreground">new</span>
                ) : null}
                {file.pendingReview === false ? (
                  <span className="text-muted-foreground hidden @[320px]/files-bar:inline">
                    applied
                  </span>
                ) : null}
                {file.linesAdded > 0 && <span className="text-green-500">+{file.linesAdded}</span>}
                {file.linesRemoved > 0 && <span className="text-red-500">-{file.linesRemoved}</span>}
              </div>
              {showDecisions ? (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onUndoFile ? onUndoFile(file.filePath) : onUndoAll?.()
                  }}
                  className="p-1 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-500"
                  title={file.pendingReview ? 'Reject' : 'Undo'}
                >
                  <X className="w-4 h-4" />
                </button>
                {file.pendingReview !== false ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onKeepFile ? onKeepFile(file.filePath) : onKeepAll?.()
                    }}
                    className="p-1 hover:bg-green-500/10 rounded text-muted-foreground hover:text-green-500"
                    title="Keep"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                ) : null}
              </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
